import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Security } from '../utils/security';

const StageCard = ({ stage, answers, onNext, isSubmitting }) => {
  const [input, setInput] = useState('');
  const MAX_LENGTH = 1000; // Reliability: Prevent massive payloads

  useEffect(() => {
    if (stage.id === 'oath' && answers.desire && answers.deadline) {
      const draft = `I commit to ${answers.desire} by ${answers.deadline}.`;
      setInput(draft);
    } else {
      setInput('');
    }
  }, [stage.id, answers.desire, answers.deadline]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const sanitizedInput = Security.sanitize(input);
    if (sanitizedInput && !isSubmitting) {
      onNext(stage.id, sanitizedInput);
    }
  };

  const ackText = stage.getAck ? stage.getAck(answers) : null;

  return (
    <motion.div
      key={stage.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full max-w-2xl mx-auto flex flex-col pt-12 pb-24 px-4"
    >
      <AnimatePresence mode="wait">
        {ackText && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-12 text-center overflow-hidden"
          >
            <p className="text-amber-500/80 font-serif italic text-lg tracking-wide px-4">
              {ackText}
            </p>
            <div className="h-px w-12 bg-amber-500/30 mx-auto mt-6"></div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-2 mb-6">
        <h2 className="text-neutral-500 uppercase tracking-[0.2em] text-[10px] font-bold">
          {stage.title}
        </h2>
        <p className="font-serif text-2xl md:text-3xl leading-relaxed text-neutral-100">
          {stage.prompt}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 mt-4">
        <div className="relative group">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, MAX_LENGTH))}
            placeholder={stage.placeholder}
            disabled={isSubmitting}
            className="w-full bg-neutral-900/50 border border-neutral-800 rounded-xl p-6 text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all min-h-[200px] text-lg leading-relaxed shadow-inner disabled:opacity-50"
            autoFocus
          />
          <div className="absolute bottom-4 right-4 text-[10px] font-mono text-neutral-600">
            {input.length}/{MAX_LENGTH}
          </div>
          
          <div className="flex items-start gap-2 mt-4 ml-2">
            <div className="w-1 h-1 rounded-full bg-amber-500/50 mt-2 shrink-0" />
            <p className="text-neutral-500 text-sm italic font-light leading-relaxed">
              {stage.hint}
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={!input.trim() || isSubmitting}
          className="self-end px-10 py-4 bg-neutral-100 text-neutral-950 font-bold rounded-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 min-w-[160px] shadow-xl"
        >
          {isSubmitting ? (
            <span className="inline-block w-5 h-5 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin"></span>
          ) : (
            'PROCEED'
          )}
        </button>
      </form>
    </motion.div>
  );
};

export default StageCard;